import { useMessageCategories } from "@/features/messages/hooks/use-messages";
import {
  BUILTIN_CATEGORY_KEYS,
  DEFAULT_MESSAGE_CATEGORIES,
} from "@/features/messages/types";
import { getColorForString } from "@/features/utils/colors";
import { useTranslation } from "@/features/utils/i18n";

const isBuiltin = (key: string) =>
  (BUILTIN_CATEGORY_KEYS as readonly string[]).includes(key);

/**
 * The tenant's categories and the name each key reads as: the label they wrote,
 * else the built-in category's translated name, else the key itself — a row keeps
 * the category it was filed under even once the tenant removed it.
 */
export function useCategoryLabels() {
  const { t } = useTranslation("messages");
  const { data: categories = DEFAULT_MESSAGE_CATEGORIES } =
    useMessageCategories();

  const labelOf = (key: string) => {
    const wanted = key || "other";
    const configured = categories.find((c) => c.key === wanted);
    if (configured?.label) return configured.label;
    return isBuiltin(wanted) ? t(`categories.${wanted}`) : wanted;
  };

  // Seeded from the key, not the label: renaming a category keeps its colour.
  const colorOf = (key: string) => getColorForString(key || "other");

  return { categories, labelOf, colorOf };
}
